import { useMutation } from '@apollo/client';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v4';
import type { UploadOutcropLithostratigraphyPageQuery } from '~/apollo/generated/v4/graphql';
import { LithostratTargetType } from '~/apollo/generated/v4/graphql';
import { SortTrigger } from '~/components/common/SortTrigger';
import { Button } from '~/components/ui/button';
import { useSortFilter } from '~/hooks/data';
import {
  uploadLithostratFormationUpdateRoute,
  uploadLithostratGroupUpdateRoute,
  uploadLithostratMemberUpdateRoute,
} from '~/paths';
import { assertExhaustive } from '~/utils/common';
import type { RefetchQueries } from '~/utils/graphql';
import { readableGqlEnum } from '~/utils/text';

const DELETE_LITHOSTRAT_OUTCROP_LINK = graphql(`
  mutation DeleteLithostratOutcropLink($id: ID!) {
    lithostratOutcropLinkDelete(id: $id) {
      result {
        id
      }
    }
  }
`);

type Outcrop = NonNullable<
  UploadOutcropLithostratigraphyPageQuery['outcropGet']
>;
type LinkType = Outcrop['lithostratOutcropLinks'][number];

function linkPath(link: LinkType) {
  invariant(link.targetType && link.targetId);

  switch (link.targetType) {
    case LithostratTargetType.Group:
      return uploadLithostratGroupUpdateRoute(link.targetId);
    case LithostratTargetType.Formation:
      return uploadLithostratFormationUpdateRoute(link.targetId);
    case LithostratTargetType.Member:
      return uploadLithostratMemberUpdateRoute(link.targetId);
    default:
      throw assertExhaustive(link.targetType);
  }
}

type Props = {
  links: LinkType[];
  refetchQueries: RefetchQueries;
};

export function OutcropLithostratLinksTable({ links, refetchQueries }: Props) {
  const { items, sortIndicatorProps: siProps } = useSortFilter(
    links,
    'targetName',
    'targetName',
    'uploadOCLithostratLinks',
  );

  const [deleteLink, { loading }] = useMutation(
    DELETE_LITHOSTRAT_OUTCROP_LINK,
    { refetchQueries },
  );

  const handleDelete = (linkId: string) => async () => {
    const confirmText = 'The selected link will be removed.';
    if (!window.confirm(confirmText)) return;

    try {
      await deleteLink({ variables: { id: linkId } });
      toast.success('Link deleted successfully.');
    } catch (err) {
      toast.error('There was a problem deleting the link.');
      console.log('Error deleting link:', err);
    }
  };

  return (
    <table className="table table-compact w-full">
      <thead>
        <tr>
          <th>
            <SortTrigger colName="targetName" sortIndicatorProps={siProps}>
              Name
            </SortTrigger>
          </th>
          <th>
            <SortTrigger
              colName="targetType"
              sortIndicatorProps={siProps}
              filterable
            >
              Lithostrat Type
            </SortTrigger>
          </th>
          <th />
        </tr>
      </thead>

      <tbody>
        {items.map(link => (
          <tr key={link.id}>
            <td>
              <Link to={linkPath(link)} className="link">
                {link.targetName}
              </Link>
            </td>
            <td>{readableGqlEnum(link.targetType)}</td>
            <td className="text-right">
              <Button
                type="button"
                color="ghost"
                size="xs"
                onClick={handleDelete(link.id)}
                loading={loading}
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
